import { useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { InvestmentEntityStatusEnumType } from '__generated__/graphql';
import dayjs from 'dayjs';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanel } from 'components/FormPanel';

import LoadingIndicator from 'primitives/LoadingIndicator';

import { getUser } from 'utils/auth';

import { countryPhoneCodes } from '../utils/country-codes';

const GET_USER_PROFILE_QUERY = gql(`
  query UserProfile($id: ID!) {
    user(id: $id) {
      id
      bio
      name
      image {
        url
        name
      }
      linkedInProfileUrl
      phoneNumber
      countryCode
      investmentEntities {
        id
        status
        createdAt
      }
    }
  }
`);

const UPDATE_USER_PROFILE_QUERY = gql(`
  mutation UpdateUserProfile($id: ID!, $bio: String, $image: Upload, $phoneNumber: String, $linkedInProfileUrl: String) {
    updateUserProfile(id: $id, bio: $bio, image: $image, phoneNumber: $phoneNumber, linkedInProfileUrl: $linkedInProfileUrl) {
      id
      bio
      image {
        url
        name
      }
      phoneNumber
      linkedInProfileUrl
    }
  }
`);

const UpdateUserProfile: FC = () => {
  const navigate = useNavigate();
  const user = getUser();

  const { data, loading, error, refetch } = useQuery(GET_USER_PROFILE_QUERY, {
    variables: { id: user.id },
  });

  const [updateUserProfile, { loading: updateLoading, error: updateError }] =
    useMutation(UPDATE_USER_PROFILE_QUERY);

  if (loading) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const investmentEntities = data.user.investmentEntities;

  const isUserWithActiveEntity = investmentEntities.some(
    entity => entity.status === InvestmentEntityStatusEnumType.Active
  );

  // If the user has no active entity, continue with the onboarding flow
  // If the user has a draft entity, prevent them from creating a new entity
  const latestDraftInvestmentEntity = [...investmentEntities]
    .sort((a, b) => dayjs(b.createdAt).diff(dayjs(a.createdAt)))
    .find(entity => entity.status === InvestmentEntityStatusEnumType.Draft);

  return (
    <FormPanel
      loading={updateLoading}
      error={updateError}
      onSubmit={formData => {
        updateUserProfile({
          variables: {
            id: user.id,
            bio: formData.bio,
            image: data?.user.image !== formData.image ? formData.image : undefined,
            phoneNumber: formData.countryCode.toString() + '-' + formData.phoneNumber.toString(),
            linkedInProfileUrl: formData.linkedInProfileUrl,
          },
        }).then(() => {
          if (isUserWithActiveEntity) {
            navigate('/');
            return;
          }
          if (latestDraftInvestmentEntity) {
            navigate(
              `/onboarding/complete-profile/investment-entity/${latestDraftInvestmentEntity.id}/basic-information`
            );
          } else {
            navigate('/onboarding/complete-profile/investment-entity');
          }
        });
      }}
      submitButtonLabel="Save and continue"
    >
      <FormInput
        fullWidth
        type="textarea"
        fieldName="bio"
        label="Bio *"
        helperText="We will use this to introduce you to other investors, funds and startups"
        defaultValue={data?.user.bio}
        placeholder="Hi! I am Rahul Sharma, and I'm deeply interested in tech and finance."
        validators={{
          required: true,
          minLength: 8,
          maxLength: 1023,
        }}
      />
      <FormInput
        fullWidth
        type="upload"
        fieldName="image"
        label="Avatar"
        helperText="Your profile picture"
        defaultValue={data?.user.image}
        maxFileSize={10}
      />
      <div className="flex flex-row w-full col-span-6 space-x-3">
        <FormInput
          fullWidth
          type="dropdown"
          options={countryPhoneCodes}
          fieldName="countryCode"
          label="Country Code *"
          defaultValue={data?.user.countryCode ?? '+91'}
          placeholder="Country code"
          validators={{
            required: true,
            isCountryCode: true,
          }}
        />
        <div className="flex-1 w-full">
          <FormInput
            fullWidth
            type="number"
            fieldName="phoneNumber"
            label="Phone Number *"
            placeholder="9999900000"
            defaultValue={data?.user.phoneNumber}
            validators={{
              required: true,
              isPhoneNumber: true,
            }}
          />
        </div>
      </div>
      <FormInput
        fullWidth
        type="text"
        fieldName="linkedInProfileUrl"
        label="LinkedIn Profile URL"
        placeholder="https://www.linkedin.com/in/rahulsharma"
        defaultValue={data?.user.linkedInProfileUrl}
        validators={{
          isLinkedinProfile: true,
        }}
      />
    </FormPanel>
  );
};

export default UpdateUserProfile;
